import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Flame, TrendingUp, Award, LogOut, Trash2, Trophy, Brain, Briefcase, Target, Rocket, Sparkles, Star, Share2, BadgeCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { CircleBoard } from "@/components/CircleBoard";
import { Switch } from "@/components/ui/switch";
import { QuestCertificate } from "@/components/QuestCertificate";
import { Billing } from "@/components/Billing";
import { InterviewBadge } from "@/components/InterviewBadge";

function TrophyShelf({ t }) {
  const [d, setD] = useState(null);
  const [badges, setBadges] = useState([]);
  const [open, setOpen] = useState(null);
  const [openBadge, setOpenBadge] = useState(null);
  useEffect(() => {
    api.get("/quests/certificates").then(({ data }) => setD(data)).catch(() => setD({ certificates: [], total_xp: 0 }));
    api.get("/interview/badges").then(({ data }) => setBadges(data.badges || [])).catch(() => {});
  }, []);
  if (!d) return null;
  const share = async () => {
    const parts = [];
    if (d.certificates.length) parts.push(`${d.certificates.length} Career Quest${d.certificates.length > 1 ? "s" : ""} completed: ${d.certificates.map((c) => c.quest).join(", ")}`);
    if (badges.length) parts.push(`Interview Ready for ${badges.map((b) => `${b.career} (${b.overall}/100)`).join(", ")}`);
    const text = parts.length ? `My NxT trophy shelf 🏆 ${parts.join(" · ")}. Discover your Career DNA: ${window.location.origin}`
      : `I'm building my career system on NxT. Discover your Career DNA: ${window.location.origin}`;
    if (navigator.share) { try { await navigator.share({ text, title: "My NxT Trophy Shelf" }); return; } catch (e) { if (e?.name === "AbortError") return; } }
    await navigator.clipboard.writeText(text); toast.success(t("copied"));
  };
  const empty = d.certificates.length === 0 && badges.length === 0;
  return (
    <div className="rounded-3xl border border-border bg-card p-5" data-testid="trophy-shelf">
      {open && <QuestCertificate questKey={open} onClose={() => setOpen(null)} />}
      {openBadge && <InterviewBadge code={openBadge} onClose={() => setOpenBadge(null)} />}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /><h2 className="font-display text-lg font-bold">{t("trophyShelf")}</h2></div>
        <span className="text-xs font-bold text-muted-foreground" data-testid="trophy-count">{d.certificates.length + badges.length} · +{d.total_xp} XP</span>
      </div>
      {empty ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="trophy-empty">{t("trophyEmpty")}</p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {badges.map((b) => (
            <button key={b.code} onClick={() => setOpenBadge(b.code)} data-testid={`badge-${b.code}`}
              className="rounded-2xl bg-gradient-to-br from-emerald-950 via-teal-900 to-emerald-600 p-3 text-left text-white transition-transform hover:-translate-y-0.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-300 bg-emerald-300/10"><BadgeCheck className="h-4 w-4 text-emerald-300" /></div>
              <div className="mt-2 line-clamp-2 text-xs font-bold leading-snug">Interview Ready · {b.career}</div>
              <div className="mt-1 font-mono text-[9px] text-white/60">{b.overall}/100 · {b.code}</div>
            </button>
          ))}
          {d.certificates.map((c) => (
            <button key={c.quest_key} onClick={() => setOpen(c.quest_key)} data-testid={`trophy-${c.quest_key}`}
              className="rounded-2xl bg-gradient-to-br from-slate-950 via-indigo-950 to-indigo-700 p-3 text-left text-white transition-transform hover:-translate-y-0.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-amber-300 bg-amber-300/10"><Award className="h-4 w-4 text-amber-300" /></div>
              <div className="mt-2 line-clamp-2 text-xs font-bold leading-snug">{c.quest}</div>
              <div className="mt-1 font-mono text-[9px] text-white/60">{c.completed_at} · {c.code}</div>
            </button>
          ))}
        </div>
      )}
      <button onClick={share} data-testid="trophy-share-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-border py-2.5 text-sm font-bold hover:bg-secondary"><Share2 className="h-4 w-4" /> {t("shareShelf")}</button>
    </div>
  );
}

function NotificationPrefs({ t }) {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.get("/notifications/preferences").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);
  if (!prefs) return null;
  const rows = [["daily_mission", t("nDaily")], ["quest_progress", t("nQuest")], ["readiness", t("nReadiness")], ["monthly_wrapped", t("nMonthly")], ["product_updates", t("nUpdates")]];
  const toggle = async (k, v) => {
    const next = { ...prefs, [k]: v }; setPrefs(next);
    try { await api.put("/notifications/preferences", next); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div id="notifications" className="rounded-3xl border border-border bg-card p-5" data-testid="notification-prefs">
      <h2 className="font-display text-lg font-bold">{t("notifications")}</h2>
      <p className="text-xs text-muted-foreground">{t("notifSub")}</p>
      <div className="mt-3 divide-y divide-border">
        {rows.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between py-2.5 text-sm font-semibold">
            <span>{label}</span><Switch checked={!!prefs[k]} onCheckedChange={(v) => toggle(k, v)} data-testid={`pref-${k}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const iconMap = { trophy: Trophy, flame: Flame, brain: Brain, briefcase: Briefcase, target: Target, rocket: Rocket, sparkles: Sparkles, star: Star };

export default function Profile() {
  const [os, setOs] = useState(null);
  const [ach, setAch] = useState([]);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLang();

  const load = () => {
    api.get("/os").then(({ data }) => setOs(data)).catch(() => {});
    api.get("/achievements").then(({ data }) => setAch(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const del = async () => {
    try { await api.post("/account/delete"); logout(); navigate("/"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!os) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 font-display text-2xl font-extrabold text-white">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div>
          <h1 className="font-display text-2xl font-extrabold">{user?.name}</h1>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          {user?.dna?.archetype && <span className="mt-1 inline-block rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold">{user.dna.archetype.name}</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-4"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("level")}</div><div className="font-display text-2xl font-extrabold"><AnimatedNumber value={os.level} /></div></div>
        <div className="rounded-2xl border border-border bg-card p-4"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("xp")}</div><div className="font-display text-2xl font-extrabold"><AnimatedNumber value={os.xp} /></div></div>
        <div className="rounded-2xl border border-border bg-card p-4"><div className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-amber-500"><Flame className="h-3.5 w-3.5" />{t("streak")}</div><div className="font-display text-2xl font-extrabold">{os.streak}d</div></div>
        <div className="rounded-2xl border border-border bg-card p-4"><div className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-emerald-500"><TrendingUp className="h-3.5 w-3.5" />{t("readiness")}</div><div className="font-display text-2xl font-extrabold">{os.readiness}%</div></div>
      </div>

      {os.target_career && <div className="rounded-2xl border border-border bg-card p-4"><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("targetCareer")}</div><div className="font-display text-lg font-bold text-indigo-600">{os.target_career}</div></div>}

      {/* subscription / billing */}
      <Billing t={t} />

      <CircleBoard compact />

      <NotificationPrefs t={t} />

      <TrophyShelf t={t} />

      {/* achievements */}
      <div>
        <div className="mb-3 flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" /><h2 className="font-display text-lg font-bold">{t("achievements")}</h2></div>
        <div className="grid grid-cols-4 gap-3">
          {ach.map((a) => {
            const Icon = iconMap[a.icon] || Trophy;
            return (
              <div key={a.key} data-testid={`achievement-${a.key}`}
                className={`flex flex-col items-center gap-1.5 rounded-2xl border p-3 text-center ${a.unlocked ? "border-amber-300 bg-amber-50" : "border-border bg-card opacity-40"}`}>
                <Icon className={`h-6 w-6 ${a.unlocked ? "text-amber-500" : "text-muted-foreground"}`} />
                <span className="text-[10px] font-bold leading-tight">{a.name}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* settings */}
      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between rounded-2xl border border-border bg-card p-4" data-testid="language-row">
          <span className="font-semibold">{t("language")}</span>
          <LangToggle />
        </div>
        <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
          className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 font-semibold transition-colors hover:bg-secondary">
          <LogOut className="h-5 w-5 text-muted-foreground" /> {t("logout")}
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="delete-account-btn" className="flex w-full items-center gap-3 rounded-2xl border border-destructive/30 bg-card p-4 font-semibold text-destructive transition-colors hover:bg-destructive/5">
              <Trash2 className="h-5 w-5" /> {t("deleteAccount")}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>This removes your account, notes, saved results and progress. Uploaded CVs become inaccessible; storage-provider retention may still apply. This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} data-testid="confirm-delete-btn" className="bg-destructive hover:bg-destructive/90">Delete forever</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <p className="pt-2 text-center text-[11px] text-muted-foreground">Your data is handled securely. Salary figures are indicative, not guaranteed.</p>
      </div>
    </div>
  );
}
